import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { configuracoes } from "../api/configuracoes";
import { obterEstabelecimentoAtivoId } from "../utils/estabelecimentoContext";
import { getProfissionalLabels } from "../utils/profissionalRotulo";
import {
  gravarRotuloProfissionalCache,
  lerRotuloProfissionalCache,
  ROTULO_PROFISSIONAL_CHANGED,
} from "../utils/profissionalRotuloCache";

const ProfissionalRotuloContext = createContext({
  labels: null,
  rotulo: null,
  carregando: true,
  atualizarRotulo: () => {},
  recarregar: () => {},
});

export const ProfissionalRotuloProvider = ({ children }) => {
  const [estabelecimentoId, setEstabelecimentoId] = useState(() =>
    obterEstabelecimentoAtivoId()
  );

  const cachedInicial = lerRotuloProfissionalCache(estabelecimentoId);
  const [rotulo, setRotulo] = useState(() =>
    cachedInicial === null ? null : cachedInicial || null
  );
  const [pronto, setPronto] = useState(() => cachedInicial !== null);

  useEffect(() => {
    const sincronizarEstabelecimento = () => {
      setEstabelecimentoId(obterEstabelecimentoAtivoId());
    };
    window.addEventListener("fz:estabelecimento:changed", sincronizarEstabelecimento);
    return () => {
      window.removeEventListener(
        "fz:estabelecimento:changed",
        sincronizarEstabelecimento
      );
    };
  }, []);

  useEffect(() => {
    const cached = lerRotuloProfissionalCache(estabelecimentoId);
    if (cached === null) {
      setRotulo(null);
      setPronto(false);
    } else {
      setRotulo(cached || null);
      setPronto(true);
    }
  }, [estabelecimentoId]);

  const aplicarRotulo = useCallback(
    (valor, { persist = true } = {}) => {
      const normalizado =
        valor === null || valor === undefined || valor === ""
          ? null
          : String(valor).trim();
      setRotulo(normalizado);
      setPronto(true);
      if (persist && estabelecimentoId) {
        gravarRotuloProfissionalCache(
          estabelecimentoId,
          normalizado,
          { silent: true }
        );
      }
    },
    [estabelecimentoId]
  );

  const recarregar = useCallback(async () => {
    if (!estabelecimentoId) {
      setRotulo(null);
      setPronto(true);
      return;
    }

    try {
      const { data } = await configuracoes.obterAgendamento(estabelecimentoId);
      aplicarRotulo(data?.rotulo_profissional ?? null);
    } catch {
      if (lerRotuloProfissionalCache(estabelecimentoId) === null) {
        setPronto(true);
        setRotulo(null);
      }
    }
  }, [estabelecimentoId, aplicarRotulo]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  useEffect(() => {
    const onConfig = () => recarregar();
    const onCache = (event) => {
      const id = Number(event?.detail?.estabelecimentoId);
      if (id && estabelecimentoId && id !== Number(estabelecimentoId)) return;
      const valor = event?.detail?.rotulo;
      setRotulo(valor || null);
      setPronto(true);
    };

    window.addEventListener("fz:configuracao:changed", onConfig);
    window.addEventListener(ROTULO_PROFISSIONAL_CHANGED, onCache);
    return () => {
      window.removeEventListener("fz:configuracao:changed", onConfig);
      window.removeEventListener(ROTULO_PROFISSIONAL_CHANGED, onCache);
    };
  }, [estabelecimentoId, recarregar]);

  const labels = useMemo(() => {
    if (!pronto) return null;
    return getProfissionalLabels({ rotulo_profissional: rotulo });
  }, [pronto, rotulo]);

  const value = useMemo(
    () => ({
      labels,
      rotulo,
      carregando: !pronto,
      atualizarRotulo: (valor) => {
        aplicarRotulo(valor);
        if (estabelecimentoId) {
          gravarRotuloProfissionalCache(estabelecimentoId, valor);
        }
      },
      recarregar,
    }),
    [labels, rotulo, pronto, aplicarRotulo, estabelecimentoId, recarregar]
  );

  return (
    <ProfissionalRotuloContext.Provider value={value}>
      {children}
    </ProfissionalRotuloContext.Provider>
  );
};

export const useProfissionalRotulo = () => useContext(ProfissionalRotuloContext);

export default ProfissionalRotuloContext;
